import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { FiUser, FiLock, FiLogIn } from 'react-icons/fi';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MySwal = withReactContent(Swal);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post('https://ethiccraft.org/backend/sign_up/login.php', {
        email,
        password,
      }, {
        withCredentials: true
      });

      if (response.status === 200 && response.data.message === "Login successful!") {
        Cookies.set('email', response.data.email, { expires: 7 });
        Cookies.set('username', response.data.username, { expires: 7 });
        Cookies.set('token', response.data.session_id, { expires: 7 });
        console.log('Login successful:', response.data);

        MySwal.fire({
          title: `<strong>Welcome, ${response.data.username}!</strong>`,
          html: 'You are being redirected to your dashboard.',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        }).then(() => {
          navigate('/dashboard');
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Login Failed',
          text: 'Invalid credentials!',
          customClass: {
            popup: 'swal-popup-custom',
            title: 'swal-title-custom',
            content: 'swal-content-custom',
          }
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'An Error Occurred',
        text: 'Try again later!',
        customClass: {
          popup: 'swal-popup-custom',
          title: 'swal-title-custom',
          content: 'swal-content-custom',
        }
      });
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    const { value: email } = await MySwal.fire({
      title: 'Enter your registered email',
      input: 'email',
      inputPlaceholder: 'your@email.com',
      showCancelButton: true,
      confirmButtonText: 'Send OTP',
      preConfirm: async (email) => {
        if (!email) return false;
        try {
          const res = await axios.post('https://ethiccraft.org/backend/forget_password/send_otp.php', { email });
          if (res.status === 200 && res.data.message?.includes('OTP')) {
            return email;
          } else {
            throw new Error('Failed to send OTP.');
          }
        } catch (err) {
          Swal.showValidationMessage(err.message);
        }
      }
    });

    if (!email) return;

    const { value: otp } = await MySwal.fire({
      title: 'Enter OTP sent to your email',
      input: 'text',
      inputPlaceholder: 'Enter OTP',
      showCancelButton: true,
      confirmButtonText: 'Verify OTP',
      preConfirm: async (otp) => {
        try {
          const res = await axios.post('https://ethiccraft.org/backend/forget_password/verify_otp.php', { email, otp });
          if (res.status === 200 && res.data.message?.includes('OTP')) {
            return otp;
          } else {
            throw new Error('Invalid OTP.');
          }
        } catch (err) {
          Swal.showValidationMessage(err.message);
        }
      }
    });

    if (!otp) return;

    const { value: newPassword } = await MySwal.fire({
      title: 'Enter your new password',
      input: 'password',
      inputPlaceholder: 'New password',
      showCancelButton: true,
      confirmButtonText: 'Reset Password',
      preConfirm: async (password) => {
        if (!password || password.length < 6) {
          Swal.showValidationMessage('Password must be at least 6 characters.');
          return false;
        }
        try {
          const res = await axios.post('https://ethiccraft.org/backend/forget_password/reset_password.php', { email, new_password: password });
          if (res.status === 200 && res.data.message?.includes('success')) {
            return true;
          } else {
            throw new Error('Failed to reset password.');
          }
        } catch (err) {
          Swal.showValidationMessage(err.message);
        }
      }
    });

    if (newPassword) {
      Swal.fire({
        icon: 'success',
        title: 'Password Reset Successful',
        text: 'You can now log in with your new password.',
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  return (
    <motion.div
      className="login-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="background-shapes">
        <div className="shape shape1"></div>
        <div className="shape shape2"></div>
        <div className="shape shape3"></div>
      </div>
      <motion.div className="login-box" variants={itemVariants}>
        <div className="login-header">
          <motion.h1
            className="brand"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Ethiccraft
          </motion.h1>
          <motion.p
            className="tagline"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            Welcome back to the Leading Society.
          </motion.p>
        </div>
        <form onSubmit={handleLogin} className="login-form">
          <motion.div className="input-group" variants={itemVariants}>
            <FiUser className="input-icon" />
            <input
              type="email"
              placeholder="Email or Username"
              className="input-box"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </motion.div>
          <motion.div className="input-group" variants={itemVariants}>
            <FiLock className="input-icon" />
            <input
              type="password"
              placeholder="Password"
              className="input-box"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </motion.div>

          <motion.div className="actions" variants={itemVariants}>
            <label className="remember-me">
              <input type="checkbox" defaultChecked />
              <span>Stay logged in</span>
            </label>
            <span className="forgot-password" onClick={handleForgotPassword} style={{ cursor: 'pointer', color: '#007bff' }}>
              Forgot?
            </span>
          </motion.div>

          <motion.button
            className="login-btn"
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            variants={itemVariants}
          >
            {isSubmitting ? (
              <div className="loader"></div>
            ) : (
              <>
                <FiLogIn />
                <span>Log In</span>
              </>
            )}
          </motion.button>

          <motion.p className="signup-link" variants={itemVariants}>
            Don't have an account? <a href="/register">Sign Up</a>
          </motion.p>
        </form>
      </motion.div>
    </motion.div>
  );
}